import type { ReviewTouchOut } from "@services/sequence/sequenceService";

export type FlaggedTouch = ReviewTouchOut & {
  sequenceId: number;
  sequenceName: string;
  /** True once Ops has edited it since the client flagged it. */
  updated: boolean;
};

type Props = {
  flagged: FlaggedTouch[];
  canResubmit: boolean;
  isSending: boolean;
  onEditCopy: (sequenceId: number, adId: number) => void;
  onResubmit: () => void;
  onClose: () => void;
};

/*
    What the client asked to be changed, and whether it has been done yet (MPN-2207).

    The client's words are shown, never hovered: a reviewer wrote a sentence about a specific
    message, and hiding it behind a tooltip is how it gets missed. Each row carries its
    comment and a way straight into that message's copy.

    Resubmit is deliberately gated on every flagged touch being updated. Sending back a
    playbook with half the requests unmet asks the client to read the same complaint twice.
*/
export function ReviewChangesPanel({
  flagged,
  canResubmit,
  isSending,
  onEditCopy,
  onResubmit,
  onClose,
}: Props) {
  const left = flagged.filter((t) => !t.updated).length;

  return (
    <aside className="esms-chg" aria-label="Review requested changes">
      <div className="esms-chg__head">
        <div>
          <div className="esms-chg__title">Changes requested</div>
          <div className="esms-chg__sub">
            {flagged.length} {flagged.length === 1 ? "touch" : "touches"} ·{" "}
            {left === 0 ? "all updated" : `${left} left to update`}
          </div>
        </div>
        <button type="button" className="esms-chg__x" aria-label="Close" onClick={onClose}>
          ×
        </button>
      </div>

      <div className="esms-chg__list">
        {flagged.map((touch) => (
          <div className="esms-chg__item" key={`${touch.sequenceId}-${touch.ad_id}`}>
            <div className="esms-chg__item-top">
              <span className={`esms-chan esms-chan--${touch.channel}`}>
                {touch.channel === "sms" ? "SMS" : "EMAIL"}
              </span>
              <span className="esms-chg__item-title">{touch.title}</span>
              {touch.updated && (
                <span className="esms-chg__done">Updated, ready to resubmit</span>
              )}
            </div>
            <div className="esms-chg__seq">{touch.sequenceName}</div>
            {touch.comment && <p className="esms-chg__note">“{touch.comment}”</p>}
            <button
              type="button"
              className="btn btn-outline-primary btn-sm esms-chg__edit"
              onClick={() => onEditCopy(touch.sequenceId, touch.ad_id)}
            >
              Edit copy
            </button>
          </div>
        ))}
      </div>

      <div className="esms-chg__foot">
        <p className="esms-chg__foot-note">
          {canResubmit
            ? "Every flagged touch is updated. Resubmit sends only the changed touches."
            : "Update every flagged touch to resubmit. Approved touches stay approved."}
        </p>
        <button
          type="button"
          className="btn btn-primary btn-sm"
          disabled={!canResubmit || isSending}
          onClick={onResubmit}
        >
          {isSending ? "Resubmitting…" : "Resubmit Changes"}
        </button>
      </div>
    </aside>
  );
}

export default ReviewChangesPanel;
